import React, { useCallback, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { FaEdit, FaPlay, FaTrashAlt } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import styled from 'styled-components';
import { archivosFiles } from '../../../../services/api';
import {
  deleteVideosAction,
  getAllVideosAction,
  setActivoVideosAction,
} from '../../../../store/actions';
import { GET_CLIENTES } from '../../../../store/types';
import useVideosHook from '../../../web/landing-news/portafolio/VideosLinksHook';

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .botones {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
  }
`;

const ImagePreview = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CategoryTag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  margin: 2px;
  background: #e9ecef;
  border-radius: 12px;
  font-size: 0.75rem;
  color: #495057;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ListadoVideos = () => {
  const dispatch = useDispatch();
  const videosState = useSelector((state) => state.videos);
  const categoriasArray = useVideosHook().categoriasArray;

  const [loadingData, setLoadingData] = useState();

  const [isModalDelete, setIsModalDelete] = useState(false);
  const [dataDelete, setDataDelete] = useState();
  const toggle = () => setIsModalDelete(false);

  const [isModalVideo, setIsModalVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const toggleVideoModal = () => setIsModalVideo(false);

  // Función para convertir links a formato embed
  const getEmbedUrl = useCallback((url) => {
    if (!url) return null;

    // Si ya es un link de embed, retornarlo
    if (
      url.includes('player.vimeo.com') ||
      url.includes('youtube.com/embed') ||
      url.includes('youtu.be')
    ) {
      return url;
    }

    // Convertir YouTube watch links a embed
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }

    // Convertir YouTube shorts
    if (url.includes('youtube.com/shorts/')) {
      const videoId = url.split('shorts/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }

    // Convertir Vimeo links
    if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
    }

    // Si es un link directo (publit.io u otros), retornarlo tal cual
    return url;
  }, []);

  const getAllVideos = useCallback(async () => {
    await getAllVideosAction(dispatch);
  }, [dispatch]);

  useEffect(() => {
    getAllVideos();
  }, [getAllVideos]);

  const columns = [
    {
      name: 'ID',
      width: '70px',
      selector: (row) => row.id,
      sortable: true,
      center: true,
    },
    {
      name: 'Foto',
      selector: (row) => row.image,
      sortable: true,
      width: '100px',
      center: true,
      cell: (row) => (
        <ImagePreview>
          <img
            src={
              row.image
                ? `${archivosFiles}/${row.image.folder}/${row.image.nombre}`
                : '../../img/movies.png'
            }
            alt="Thumbnail"
          />
        </ImagePreview>
      ),
    },
    {
      name: 'Foto EN',
      selector: (row) => row.imageEN,
      sortable: true,
      width: '100px',
      center: true,
      cell: (row) => (
        <ImagePreview>
          <img
            src={
              row.imageEN
                ? `${archivosFiles}/${row.imageEN.folder}/${row.imageEN.nombre}`
                : '../../img/movies.png'
            }
            alt="Thumbnail EN"
          />
        </ImagePreview>
      ),
    },
    {
      name: 'Título / Tipo',
      width: '220px',
      sortable: true,
      selector: (row) => row.titulo,
      wrap: true,
      cell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ fontWeight: '500' }}>{row.titulo}</div>
          <CategoryTag style={{ background: '#cfe2ff', color: '#084298' }}>
            {row.tipo}
          </CategoryTag>
        </div>
      ),
    },
    {
      name: 'Categorías',
      sortable: false,
      cell: (row) => {
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {row.categorias
              ?.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
              .map((el) => {
                const item = categoriasArray.find((cat) => cat.id === el);
                return item ? (
                  <CategoryTag key={item.id}>{item.label}</CategoryTag>
                ) : null;
              })}
          </div>
        );
      },
    },
    {
      name: 'Descripción',
      width: '200px',
      selector: (row) => row.desc,
      wrap: true,
      style: {
        fontSize: '0.875rem',
        color: '#6c757d',
      },
    },
    {
      name: 'Posición',
      width: '100px',
      selector: (row) => row.posicion,
      sortable: true,
      center: true,
    },
    {
      name: 'Links (ES / EN)',
      sortable: false,
      cell: (row) => {
        const embedUrlES = getEmbedUrl(row.link);
        const embedUrlEN = getEmbedUrl(row.linkEN);

        return (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            {/* Link ES */}
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
            >
              {!embedUrlES || !row.link ? (
                <div style={{ color: '#6c757d', fontSize: '0.75rem' }}>
                  Sin link
                </div>
              ) : (
                <>
                  <Button
                    size="sm"
                    color="info"
                    onClick={(e) => {
                      e.stopPropagation();
                      setVideoUrl(embedUrlES);
                      setIsModalVideo(true);
                    }}
                    style={{
                      fontSize: '0.7rem',
                      padding: '2px 8px',
                      marginTop: '5px',
                    }}
                  >
                    <FaPlay style={{ marginRight: '4px' }} />
                    Ver (ES)
                  </Button>
                  <a
                    href={row.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '0.7rem',
                      color: '#007bff',
                      textDecoration: 'none',
                      wordBreak: 'break-all',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={(e) => {
                      e.target.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.textDecoration = 'none';
                    }}
                  >
                    {row.link.length > 40
                      ? `${row.link.substring(0, 40)}...`
                      : row.link}
                  </a>
                </>
              )}
            </div>

            {/* Link EN */}
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
            >
              {!embedUrlEN || !row.linkEN ? (
                <div style={{ color: '#6c757d', fontSize: '0.75rem' }}>
                  Sin link
                </div>
              ) : (
                <>
                  <Button
                    size="sm"
                    color="info"
                    onClick={(e) => {
                      e.stopPropagation();
                      setVideoUrl(embedUrlEN);
                      setIsModalVideo(true);
                    }}
                    style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                  >
                    <FaPlay style={{ marginRight: '4px' }} />
                    Ver (EN)
                  </Button>
                  <a
                    href={row.linkEN}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '0.7rem',
                      color: '#007bff',
                      textDecoration: 'none',
                      wordBreak: 'break-all',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={(e) => {
                      e.target.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.textDecoration = 'none';
                    }}
                  >
                    {row.linkEN.length > 40
                      ? `${row.linkEN.substring(0, 40)}...`
                      : row.linkEN}
                  </a>
                </>
              )}
            </div>
          </div>
        );
      },
    },
    {
      name: 'Acciones',
      width: '140px',
      cell: (row) => (
        <ActionButtons>
          <Button
            size="sm"
            color="primary"
            onClick={async (e) => {
              e.stopPropagation();
              await setActivoVideosAction(dispatch, row);
            }}
            title="Editar"
          >
            <FaEdit />
          </Button>
          <Button
            size="sm"
            color="danger"
            onClick={(e) => {
              e.stopPropagation();
              setDataDelete(row);
              setIsModalDelete(true);
            }}
            title="Eliminar"
          >
            <FaTrashAlt />
          </Button>
        </ActionButtons>
      ),
    },
  ];

  const paginationComponentOptions = {
    rowsPerPageText: 'Filas por página',
    rangeSeparatorText: 'de',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Todos',
  };

  const fetchUsers = async (page) => {
    setLoadingData(true);
    const res = await getAllVideosAction(page, videosState.meta.itemsPerPage);
    dispatch({
      type: GET_CLIENTES,
      payload: res,
    });
    setLoadingData(false);
  };

  const handlePageChange = (page) => {
    fetchUsers(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setLoadingData(true);
    const res = await getAllVideosAction(page, newPerPage);
    dispatch({
      type: GET_CLIENTES,
      payload: res,
    });
    setLoadingData(false);
  };

  const customStyles = {
    table: {
      style: {
        width: '100%',
      },
    },
    tableWrapper: {
      style: {
        width: '100%',
        overflowX: 'auto',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#343a40',
        color: '#fff',
        fontSize: '0.875rem',
        fontWeight: '600',
        minHeight: '52px',
      },
    },
    headCells: {
      style: {
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    cells: {
      style: {
        paddingLeft: '16px',
        paddingRight: '16px',
        fontSize: '0.875rem',
      },
    },
    rows: {
      style: {
        minHeight: '72px',
        '&:hover': {
          backgroundColor: '#f8f9fa',
        },
      },
      stripedStyle: {
        backgroundColor: '#f8f9fa',
      },
    },
  };

  return (
    <>
      <Container>
        <DataTable
          columns={columns}
          data={videosState.all}
          pagination
          paginationComponentOptions={paginationComponentOptions}
          dense
          striped
          pointerOnHover
          highlightOnHover
          customStyles={customStyles}
          fixedHeader
          fixedHeaderScrollHeight="600px"
          responsive
          onRowClicked={async (row, event) => {
            // Evitar abrir el modal si se hace clic en botones o previews
            if (
              event.target.closest('button') ||
              event.target.closest('.video-preview') ||
              event.target.closest('a')
            ) {
              return;
            }
            await setActivoVideosAction(dispatch, row);
          }}
          paginationPerPage={
            videosState.meta !== null && videosState.meta !== undefined
              ? videosState.meta.itemsPerPage
              : 50
          }
          paginationRowsPerPageOptions={[10, 30, 50]}
          paginationTotalRows={
            videosState.meta !== null && videosState.meta !== undefined
              ? videosState.meta.totalItems
              : 0
          }
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          paginationServer
          progressPending={loadingData}
        />
      </Container>

      {/* Modal para ver video completo */}
      <Modal
        isOpen={isModalVideo}
        toggle={toggleVideoModal}
        centered
        size="lg"
        className="modal-video-preview"
      >
        <ModalHeader toggle={toggleVideoModal}>
          Vista Previa del Video
        </ModalHeader>
        <ModalBody>
          <div
            style={{
              position: 'relative',
              width: '100%',
              paddingBottom: '56.25%',
              height: 0,
              overflow: 'hidden',
            }}
          >
            <iframe
              title="Video completo"
              src={videoUrl}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleVideoModal}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal isOpen={isModalDelete} toggle={toggle}>
        <ModalHeader toggle={toggle}>Eliminar Video</ModalHeader>
        <ModalBody>
          <p>¿Seguro que deseas eliminar este video?</p>
          <h4 style={{ marginTop: '10px', color: '#dc3545' }}>
            {dataDelete ? dataDelete.titulo : ''}
          </h4>
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={async () => {
              await deleteVideosAction(dispatch, dataDelete.id);
              toggle();
            }}
          >
            Eliminar
          </Button>{' '}
          <Button color="secondary" onClick={toggle}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ListadoVideos;
